import { useState, useEffect, useCallback, useRef } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useTranslation } from '@/i18n';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';
import type { RecalculationProcessStatus } from '@/api/domains/recalculation';


const POLLING_INTERVAL_MS = 5000;
export const PENDING_STATUS = 'Pendiente';
export const IN_PROGRESS_STATUS = 'En curso';
export const ERROR_STATUS = 'Hubo un error';
export const FINISHED_STATUS = 'Finalizado';
export const STOPPED_STATUS = 'Detenido';

export interface UseRecalculationStatusResult {
  isRecalculating: boolean;
  processStatus: RecalculationProcessStatus | null;
}

export function useRecalculationStatus(): UseRecalculationStatusResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [processStatus, setProcessStatus] = useState<RecalculationProcessStatus | null>(null);
  const hasErrorRef = useRef(false);
  const intervalRef = useRef<number | null>(null);

  const fetchStatus = useCallback(async () => {
    const signal = createSignal();
    const response = await recalculationMonthlyCutsService.getProcessStatus(signal);

    if (response.error?.code === 'ABORTED') return;

    if (!response.success || !response.data) {
      if (!hasErrorRef.current) {
        hasErrorRef.current = true;
        showApiError(response, translate('attendance:recalculo.errors.loadStatus'));
      }
      return;
    }

    hasErrorRef.current = false;
    const isActive = response.data.status === PENDING_STATUS
      || response.data.status === IN_PROGRESS_STATUS;
    setIsRecalculating(isActive);
    setProcessStatus(response.data);

    if (!isActive && intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [createSignal, showApiError, translate]);

  useEffect(() => {
    void fetchStatus();
    intervalRef.current = window.setInterval(() => void fetchStatus(), POLLING_INTERVAL_MS);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStatus]);

  return { isRecalculating, processStatus };
}

export default useRecalculationStatus;
